import type { Ticket } from '@rtkwlf/rendall';
import { TicketStatus } from '@rtkwlf/rendall';
import type { ManipulateType } from 'dayjs';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import queryString from 'query-string';

import { TicketStatusOptions } from '@packages/legacy/ticket-status';
import { isNotEmpty } from '@packages/utils/base.utils';
import type { ItemType } from 'ae-components/dropdownSelect/SelectElements';

/**
 * A map of the displayed ticket type options to the possible API type values.
 * Can be seen as a reverse mapping of switch statements in `<TicketStatus/>`.
 */
const ticketStatusFilterMap: Record<TicketStatusOptions, TicketStatus[]> = {
  [TicketStatusOptions.Open]: [
    TicketStatus.New,
    TicketStatus.Open,
    TicketStatus.Hold,
    TicketStatus.Pending,
  ],
  [TicketStatusOptions.WithAw]: [
    TicketStatus.New,
    TicketStatus.Open,
    TicketStatus.Hold,
  ],
  [TicketStatusOptions.WithCustomer]: [TicketStatus.Pending],
  [TicketStatusOptions.Closed]: [TicketStatus.Solved, TicketStatus.Closed],
  [TicketStatusOptions.All]: [
    TicketStatus.New,
    TicketStatus.Open,
    TicketStatus.Hold,
    TicketStatus.Pending,
    TicketStatus.Solved,
    TicketStatus.Closed,
  ],
};

export enum TicketTypeOption {
  Customization = 'customization',
  Incident = 'incident',
  Information = 'info',
  Onboarding = 'onboarding',
  Provisioning = 'provisioning',
  Question = 'question',
  Request = 'request',
  Scheduling = 'schedule',
  Security = 'security',
  Support = 'support',
  Validation = 'validation',
  Other = 'other',
}

const APITicketTypes = Object.values(TicketTypeOption).filter(
  (t) => t !== TicketTypeOption.Other,
) as string[];

// The order of the options should from short to long one
// The prop: ticketLastUpdatedOrdered is generated by  TicketLastUpdatedOptions.
export enum TicketLastUpdatedOptions {
  Past72Hours = 'past72Hours',
  Past7Days = 'past7Days',
  Past10Days = 'past10Days',
  Past30Days = 'past30Days',
  Past3Months = 'past3Months',
  Past12Months = 'past12Months',
  Anytime = 'anytime',
}

export type FilterValType = [number, ManipulateType] | null;
export const ticketLastUpdatedFilterMap: Record<
  TicketLastUpdatedOptions,
  FilterValType
> = {
  [TicketLastUpdatedOptions.Anytime]: null,
  [TicketLastUpdatedOptions.Past72Hours]: [72, 'hours'],
  [TicketLastUpdatedOptions.Past7Days]: [7, 'days'],
  [TicketLastUpdatedOptions.Past10Days]: [10, 'days'],
  [TicketLastUpdatedOptions.Past30Days]: [30, 'days'],
  [TicketLastUpdatedOptions.Past3Months]: [3, 'months'],
  [TicketLastUpdatedOptions.Past12Months]: [12, 'months'],
};

// used to calculate the before/after time higher order should have longer time range.
export const ticketLastUpdatedOrdered = Object.values(TicketLastUpdatedOptions);

export type TicketFiltersObject = {
  search?: string;
  type?: TicketTypeOption;
  status?: TicketStatusOptions;
  lastUpdated?: TicketLastUpdatedOptions;
};

type FilterKey = keyof TicketFiltersObject;

export enum TicketFiltersEnum {
  Type = 'type',
  Status = 'status',
  LastUpdated = 'lastUpdated',
}

export type TicketFilterType = {
  field: keyof TicketFiltersObject;
  label: string;
  options: Array<ItemType>;
  placeholderKey: string;
};

type SearchFieldsType = keyof Ticket;

const searchFields: SearchFieldsType[] = ['id', 'subject', 'description'];

export const getTicketFilters = (t: TFunction): TicketFilterType[] => [
  {
    field: TicketFiltersEnum.Type,
    label: t('ticket:details.ticketType'),
    options: Object.values(TicketTypeOption).map((type) => ({
      label: t(`ticket:ticketTypes.${type}`),
      value: type,
    })),
    placeholderKey: t('ui:dropDown.all'),
  },
  {
    field: TicketFiltersEnum.Status,
    label: t('ticket:details.status'),
    options: [
      {
        label: t('ticket:ticketStatus:open'),
        value: TicketStatusOptions.Open,
      },
      {
        label: t('ticket:ticketStatus:withAW'),
        value: TicketStatusOptions.WithAw,
      },
      {
        label: t('ticket:ticketStatus:withCustomer'),
        value: TicketStatusOptions.WithCustomer,
      },
      {
        label: t('ticket:ticketStatus:closed'),
        value: TicketStatusOptions.Closed,
      },
      {
        label: t('ui:dropDown.all'),
        value: TicketStatusOptions.All,
      },
    ],
    placeholderKey: t('ui:dropDown.all'),
  },
  {
    field: TicketFiltersEnum.LastUpdated,
    label: t('ticket:details.lastUpdated'),
    options: [
      {
        label: t('datePicker:anytime'),
        value: TicketLastUpdatedOptions.Anytime,
      },
      {
        label: t('datePicker:past72'),
        value: TicketLastUpdatedOptions.Past72Hours,
      },
      {
        label: t('datePicker:past7Days'),
        value: TicketLastUpdatedOptions.Past7Days,
      },
      {
        label: t('datePicker:past10Days'),
        value: TicketLastUpdatedOptions.Past10Days,
      },
      {
        label: t('datePicker:past30Days'),
        value: TicketLastUpdatedOptions.Past30Days,
      },
      {
        label: t('datePicker:past3Months'),
        value: TicketLastUpdatedOptions.Past3Months,
      },
      {
        label: t('datePicker:past12Months'),
        value: TicketLastUpdatedOptions.Past12Months,
      },
    ],
    placeholderKey: t('ui:dropDown.allTime'),
  },
];

export const ticketInitialFilter = {
  lastUpdated: TicketLastUpdatedOptions.Past30Days,
  status: TicketStatusOptions.Open,
};

export const getLastUpdatedFromQuery = (
  input: string,
): TicketLastUpdatedOptions => {
  let { lastUpdated } = ticketInitialFilter;

  Object.entries(TicketLastUpdatedOptions).forEach(([key, value]) => {
    if (value === input) {
      lastUpdated =
        TicketLastUpdatedOptions[key as keyof typeof TicketLastUpdatedOptions];
    }
  });

  return lastUpdated;
};

export const filterTickets = (
  tickets: Required<Ticket>[],
  { search, type, status, lastUpdated }: TicketFiltersObject,
): Required<Ticket>[] => {
  let res = tickets;
  if (search) {
    // search text for specific columns for each row
    res = res.filter((t) => {
      const searchableText = searchFields
        .map((fieldKey) => t[fieldKey].toString())
        .join(' ')
        .toLocaleLowerCase();
      const s = search.toLocaleLowerCase();
      return searchableText.includes(s);
    });
  }

  if (type) {
    if (type !== TicketTypeOption.Other) {
      res = res.filter((t) => t.type === type);
    } else {
      res = res.filter((t) => !APITicketTypes.includes(t.type));
    }
  }

  if (status) {
    const apiStatuses = ticketStatusFilterMap[status];
    res = res.filter((t) => apiStatuses.includes(t.status));
  }

  if (lastUpdated) {
    const params = ticketLastUpdatedFilterMap[lastUpdated];
    if (params) {
      const [value, unit] = params;
      res = res.filter((t) =>
        dayjs.utc().subtract(value, unit).isBefore(t.updatedAt),
      );
    }
  }

  return res;
};

/**
 *
 * @param search the search string from the url
 * @param filters current filters used for ticket list
 * @returns Ticket filter Object with validate values.
 */
export const getValidateFilters = (
  search: string,
  filters: TicketFilterType[],
): TicketFiltersObject => {
  const searchParams = queryString.parse(search) as TicketFiltersObject;
  const initFilters: TicketFiltersObject = {
    lastUpdated: TicketLastUpdatedOptions.Past30Days,
    status: TicketStatusOptions.Open,
  };
  const keys = Object.keys(searchParams) as unknown as FilterKey[];
  keys.forEach((key) => {
    const value = searchParams[key] ?? '';
    if (key === 'search') {
      initFilters[key] = value;
    } else {
      const filter = filters.find((obj) => obj.field === key);

      const option = filter?.options.find(
        (opt: ItemType) => opt.value === value,
      );

      if (isNotEmpty(option) && option?.value) {
        (initFilters[key] as string) = option.value;
      }
    }
  });
  return initFilters;
};
